import {
  AZURE_AUTH_ENABLED,
  AZURE_CLIENT_ID,
  AZURE_TENANT_ID,
  AZURE_SCOPES
} from '@/settingsFieldwork'

import type {
  AccountInfo,
  AuthenticationResult,
  Configuration, EndSessionRequest,
  SilentRequest
} from '@azure/msal-browser'

import {
  PublicClientApplication
} from '@azure/msal-browser'
import router from '@/router'

import { useMainCMStore } from '@/stores/CapacityManagerStore'
import * as Sentry from '@sentry/browser'
import { clearDismissedAlertHashes, clearDismissedErrorIds } from '@/helpers/alertBanner'
import * as localforage from 'localforage'

/**
 * AZURE auth settings
 */
const msalConfig: Configuration = {
  auth: {
    clientId: AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${AZURE_TENANT_ID}/`,
    redirectUri: `${window.location.protocol}//${window.location.hostname}`,
    postLogoutRedirectUri: `${window.location.protocol}//${window.location.hostname}`,
    navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: 'localStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false // Set this to 'true' if you are having issues on IE11 or Edge
  }
}
const msal = new PublicClientApplication(msalConfig)

/**
 * Initializes MSAL and handles the redirect response (if any) from Azure login.
 * Must be called and awaited before mounting the app, so that userIsLoggedIn()
 * returns the correct state when components mount.
 */
export async function initMsal (): Promise<void> {
  await msal.initialize()

  const response = await msal.handleRedirectPromise()
  if (response) {
    handleLoginResponse(response)
  }
}

/**
 * Processes the authentication result returned after a redirect login.
 * Sets the active account and triggers post-login actions if the user has roles,
 * or flags a no-roles error and redirects to the login page.
 */
function handleLoginResponse (response: AuthenticationResult): void {
  const account = response.account
  const roles = account?.idTokenClaims?.roles

  if (account && roles) {
    window.sessionStorage.removeItem('fieldwork.showNoRolesError')
    msal.setActiveAccount(response.account)
    redirectAfterLogin()

    const ev = new CustomEvent('azureLoginSuccess')
    document.dispatchEvent(ev)

    clearDismissedErrorIds()
    clearDismissedAlertHashes()

    const store = useMainCMStore()
    store.setDefaultWorkSupplier()
    store.getErrorsForAlertBanner()
  } else {
    if (typeof roles === 'undefined' || roles.length === 0) {
      window.sessionStorage.setItem('fieldwork.showNoRolesError', 'true')
      redirectToLogin()
    }
  }
}

export function getAccount (): AccountInfo | null {
  const account = msal.getActiveAccount()
  if (account) {
    return account
  }

  return null
}

export function userIsLoggedIn (): boolean {
  let userIsLoggedIn = true
  if (AZURE_AUTH_ENABLED) {
    const loggedInAccount = msal.getActiveAccount()

    userIsLoggedIn = loggedInAccount !== null
  }
  return userIsLoggedIn
}

function redirectAfterLogin (): void {
  localforage.getItem<string>('redirectURL').then((redirectURL) => {
    switch (redirectURL) {
      case '/fault/':
        router.push({ name: 'FMP-Home' })
        break
      case '/schedule/':
        router.push({ name: 'SM-ScheduleManager' })
        break

      case '/capacity/':
        router.push({ name: 'CM-Home' })
        break

      default:
        router.push({ name: 'FW-Home' })
        break
    }
    localforage.removeItem('redirectURL')
  })
}

export function redirectToLogin (): void {
  // eslint-disable-next-line
  // @ts-ignore
  if (router.currentRoute.name !== 'FW-Login') {
    router.push({ name: 'FW-Login' })
  }
}

type PromptType = 'login' | 'none' | 'consent' | 'select_account'
const prompt: PromptType = 'login'

export function login (): void {
  const loginRequest = {
    scopes: AZURE_SCOPES,
    prompt
  }

  msal.clearCache().then(() => {
    msal.loginRedirect(loginRequest)
      .catch((reason) => {
        Sentry.captureException(reason)
        redirectToLogin()
      })
  })
}

export function getUserInitials (): string {
  const account = getAccount()
  if (account && account.name) {
    const firstAndLastName = account.name.split(' ')
    if (firstAndLastName.length === 1) {
      return firstAndLastName[0][0].toLocaleUpperCase()
    }
    return firstAndLastName
      .map((name) => name[0])
      .join('')
      .toLocaleUpperCase()
  } else {
    return ' - '
  }
}

export function getUsersUserName (): string {
  const account = getAccount()
  if (account && account.username) {
    return account.username
  } else {
    return ''
  }
}

export function getUserName (): string {
  const account = getAccount()
  if (account && account.name) {
    return account.name
  } else {
    return ''
  }
}

export function getUserCompanyName (): string {
  const account = getAccount()
  if (account && account.idTokenClaims && account.idTokenClaims.company) {
    return account.idTokenClaims.company as string
  } else {
    return '-'
  }
}

export function userCompanyIsChorus (): boolean {
  if (!AZURE_AUTH_ENABLED) {
    return true
  }
  return getUserCompanyName().toLowerCase().includes('chorus')
}

export function userCompanyIsUCG (): boolean {
  if (!AZURE_AUTH_ENABLED) {
    return false
  }
  return getUserCompanyName().toLowerCase().includes('ucg')
}

export function userCompanyIsDowner () : boolean {
  if (!AZURE_AUTH_ENABLED) {
    return false
  }
  return getUserCompanyName().toLowerCase().includes('downer')
}

export function userHasCMReadOnlyRole (): boolean {
  if (!AZURE_AUTH_ENABLED) {
    return true
  }
  const account = getAccount()
  if (account && account.idTokenClaims) {
    const roles = account.idTokenClaims.roles as string[]
    return roles.includes('CapacityManagerReadOnly')
  }
  return false
}

export function userHasCMAdminRole (): boolean {
  if (!AZURE_AUTH_ENABLED) {
    return true
  }
  const account = getAccount()
  if (account && account.idTokenClaims) {
    const roles = account.idTokenClaims.roles as string[]
    return roles.includes('CapacityManagerAdmin')
  }
  return false
}

export function userHasCMUpdaterRole (): boolean {
  if (!AZURE_AUTH_ENABLED) {
    return true
  }
  const account = getAccount()
  if (account && account.idTokenClaims) {
    const roles = account.idTokenClaims.roles as string[]
    return roles.includes('CapacityManagerUpdater')
  }
  return false
}

export function userHasSMReadOnlyRole (): boolean {
  if (!AZURE_AUTH_ENABLED) {
    return true
  }
  const account = getAccount()
  if (account && account.idTokenClaims) {
    const roles = account.idTokenClaims.roles as string[]
    return roles.includes('ScheduleManagerReadOnly')
  }
  return false
}

export function userHasSMAdminRole (): boolean {
  if (!AZURE_AUTH_ENABLED) {
    return true
  }
  const account = getAccount()
  if (account && account.idTokenClaims) {
    const roles = account.idTokenClaims.roles as string[]
    return roles.includes('ScheduleManagerAdmin')
  }
  return false
}

export function userHasFaultManagerUserRole (): boolean {
  if (!AZURE_AUTH_ENABLED) {
    return true
  }
  const account = getAccount()
  if (account && account.idTokenClaims) {
    const roles = account.idTokenClaims.roles as string[]
    return roles.includes('FaultManagementUser')
  }
  return false
}

export function userHasRole (role: string): boolean {
  if (!AZURE_AUTH_ENABLED) {
    return false
  }
  const account = getAccount()
  if (account && account.idTokenClaims) {
    const roles = account.idTokenClaims.roles as string[]
    return roles.includes(role)
  }
  return false
}

// NRM role checks
export function userHasNRMChorusAdminRole (): boolean {
  return userHasRole('FieldWork.NRDChorusAdmin')
}

export function userHasNRMReadOnlyRole (): boolean {
  return userHasRole('FieldWork.NRDReadOnly')
}

export function userHasNRMSerCoAdminRole (): boolean {
  return userHasRole('FieldWork.NRDSercoAdmin')
}

export function userHasNRMSerCoFieldRole (): boolean {
  return userHasRole('FieldWork.NRDSercoField')
}

export function userHasNRMScrapMetalRole (): boolean {
  return userHasRole('FieldWork.NRDScrapperAdmin')
}

// PLANT role checks
export function userHasPlantAlmViewerRole (): boolean {
  return userHasRole('ADDRESSLOCATIONMANAGERVIEWER')
}

export function userHasPlantAlmEditorRole (): boolean {
  return userHasRole('ADDRESSLOCATIONMANAGEREDITOR')
}

export function userHasPlantAlmAdminRole (): boolean {
  return userHasRole('ADDRESSLOCATIONMANAGERADMIN')
}

export function userHasPlantSiteManagerViewerRole (): boolean {
  return userHasRole('SITEMANAGERVIEWER')
}

export function userHasPlantSiteManagerEditorRole (): boolean {
  return userHasRole('SITEMANAGEREDITOR')
}

export function userHasPlantSiteManagerAdminRole (): boolean {
  return userHasRole('SITEMANAGERADMIN')
}

export function userHasPlantSitePropertyEditorRole (): boolean {
  return userHasRole('SITEMANAGERPROPERTYEDITOR')
}

export async function getToken (forceRefresh = false): Promise<string> {
  const account = getAccount()
  let token = null as (null | string)
  if (account !== null) {
    const silentTokenRequest: SilentRequest = {
      account,
      scopes: AZURE_SCOPES,
      forceRefresh // Force a network request to fetch token from azure instead of local storage
    }
    await msal.acquireTokenSilent(silentTokenRequest)
      .then(response => {
        token = response.accessToken

        useMainCMStore().setDefaultWorkSupplier()
      })
      .catch(() => {
        // Silent token refresh failed.
        redirectToLogin()
      })
  }
  return token ?? await Promise.reject(Error('Session expired!'))
}

export async function getTokenFromAzure (): Promise<string> {
  return await getToken(true)
}

export function logout (): Promise<void> {
  if (AZURE_AUTH_ENABLED) {
    const account = getAccount()
    const request: EndSessionRequest = {
      account: account ?? undefined
    }
    return msal.logoutRedirect(request)
  }
  return Promise.resolve()
}
